<template>
  <article class="sclass">
    <!-- ===== Cabeçalho: identidade da turma ===== -->
    <header class="sclass__head">
      <div class="sclass__id">
        <div class="sclass__eyebrow">
          {{ $t("classView.eyebrow", { code: classData.cod || "—" }) }}
        </div>
        <h2 class="sclass__level sclass-display">
          {{ levelLabel }}
        </h2>
        <div class="sclass__meta">
          {{ formatLabel
          }}<template v-if="!isAutonomy"> · {{ frequencyLabel }}</template
          ><template v-if="sinceLabel">
            {{ $t("classView.since", { date: sinceLabel }) }}</template
          >
        </div>
      </div>

      <span class="sclass__status" :class="`sclass__status--${statusTone}`">
        <span class="sclass__status-dot"></span>{{ statusLabel }}
      </span>
    </header>

    <!-- ===== Quando se encontra + quem ensina ===== -->
    <div v-if="!isAutonomy" class="sclass__grid">
      <section class="sclass__block">
        <div class="sclass__block-title">{{ $t("classView.meets") }}</div>

        <div v-if="schedule.length" class="sclass__slots">
          <div
            v-for="(time, i) in schedule"
            :key="`t-${i}`"
            class="sclass__slot"
          >
            <span class="sclass__slot-day">{{
              weekDayShort(time.weekDay)
            }}</span>
            <span class="sclass__slot-time"
              >{{ time.start }}<span class="sclass__slot-dash">–</span
              >{{ time.end }}</span
            >
          </div>
        </div>
        <div v-else class="sclass__muted">
          {{ $t("classView.noSchedule") }}
        </div>
      </section>

      <section class="sclass__block">
        <div class="sclass__block-title">{{ $t("classView.teaches") }}</div>

        <div class="sclass__teacher">
          <span class="sclass__avatar sclass__avatar--teacher">
            <img
              v-if="teacher.avatar"
              :src="teacher.avatar"
              :alt="teacher.name"
            />
            <span v-else>{{ teacher.initials }}</span>
          </span>
          <div>
            <div class="sclass__teacher-name">{{ teacher.name }}</div>
            <div class="sclass__muted">{{ $t("classView.inCharge") }}</div>
          </div>
        </div>
      </section>
    </div>

    <!-- ===== Colegas ===== -->
    <section class="sclass__peers">
      <div class="sclass__block-title">{{ $t("classView.peers") }}</div>

      <div v-if="peers.length" class="sclass__peers-row">
        <span
          v-for="peer in peers"
          :key="peer.key"
          class="sclass__peer"
          :class="{ 'sclass__peer--self': peer.isSelf }"
          :title="peer.name"
        >
          <span class="sclass__avatar">
            <img v-if="peer.avatar" :src="peer.avatar" :alt="peer.name" />
            <span v-else>{{ peer.initials }}</span>
          </span>
          <span class="sclass__peer-name">{{ peer.label }}</span>
        </span>
      </div>

      <div class="sclass__peers-caption">{{ peersCaption }}</div>
    </section>

    <!-- ===== Minha frequência (assinatura: a fita) ===== -->
    <!-- Card 86aknajnn: a turma Autonomy não tem aulas com professor, então
         não há chamada de turma para mostrar. A frequência dela é a de
         conversação e monitoria, que a página de turmas já mostra logo abaixo,
         separada por tipo e filtrada por mês. -->
    <section v-if="!isAutonomy" class="sclass__freq">
      <div class="sclass__freq-head">
        <div class="sclass__block-title">
          {{ $t("classView.myAttendance") }}
        </div>

        <v-select
          v-if="monthOptions.length"
          :value="selectedAttendanceMonthKey"
          :items="monthOptions"
          item-text="text"
          item-value="value"
          dense
          outlined
          hide-details
          class="sclass__month"
          :disabled="loadingAttendance"
          @change="handleAttendanceMonthChange"
        />
      </div>

      <page-skeleton
        v-if="loadingAttendance"
        variant="rows"
        :rows="3"
        class="mt-2"
      />

      <div v-else-if="!monthOptions.length" class="sclass__muted mt-2">
        {{ $t("classView.noRecords") }}
      </div>

      <template v-else>
        <div class="sclass__freq-body">
          <div class="sclass__score">
            <span class="sclass__score-value sclass-display">{{
              scorePct
            }}</span>
            <span class="sclass__score-label">{{
              $t("classView.ofAttendance")
            }}</span>
          </div>

          <div class="sclass__strip">
            <div
              v-for="ev in attendanceEvents"
              :key="ev.id"
              class="sclass__cell"
              :class="`sclass__cell--${cellState(ev)}`"
              :title="`${cellStatusLabel(ev)} · ${cellFullDate(ev)}`"
            >
              <span class="sclass__cell-wd">{{ cellWeekday(ev) }}</span>
              <span class="sclass__cell-day">{{ cellDay(ev) }}</span>
            </div>

            <div v-if="!attendanceEvents.length" class="sclass__muted">
              {{ $t("classView.noLessonsMonth") }}
            </div>
          </div>
        </div>

        <div class="sclass__legend">
          <span class="sclass__tag sclass__tag--present">{{
            $tc("classView.presences", summary.presentCount, {
              count: summary.presentCount,
            })
          }}</span>
          <span class="sclass__tag sclass__tag--absent">{{
            $tc("classView.absences", summary.absentCount, {
              count: summary.absentCount,
            })
          }}</span>
          <span
            v-if="summary.pendingCount"
            class="sclass__tag sclass__tag--pending"
            >{{
              $tc("attendance.pending", summary.pendingCount, {
                count: summary.pendingCount,
              })
            }}</span
          >
          <span
            v-if="summary.futureCount"
            class="sclass__tag sclass__tag--future"
            >{{
              $tc("attendance.future", summary.futureCount, {
                count: summary.futureCount,
              })
            }}</span
          >
        </div>

        <div class="sclass__note">
          {{ $t("classView.note") }}
        </div>
      </template>
    </section>
  </article>
</template>

<script>
import customerApi from "@/shared/pitchCustomerApi";
import {
  displayGroupLevel,
  displayGroupFormat,
  displayGroupFrequency,
  displayGroupStatus,
  initialsFromName,
  normalizeClassTimes,
  weekDayShortLabel,
} from "@/shared/classPresentation";
import { formatMonthYear, languageTag } from "@/plugins/i18n";

export default {
  name: "ClassView",

  props: {
    item: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      loadingAttendance: false,
      attendanceRequestToken: 0,
      attendanceAvailableMonths: [],
      attendanceEvents: [],
      selectedAttendanceMonthKey: null,
    };
  },

  watch: {
    "item.id": {
      immediate: true,
      async handler() {
        await this.loadAttendance();
      },
    },
  },

  computed: {
    classData() {
      return this.item || {};
    },
    levelLabel() {
      return displayGroupLevel(this.classData.level);
    },
    // Card 86ajafbvh: a turma do aluno Autonomy é montada pela API, não existe
    // no banco. Ela tem código e nível, mas não tem professor, frequência nem
    // cronograma — os blocos que dependem disso não são renderizados.
    isAutonomy() {
      return !!this.classData.isAutonomy;
    },
    formatLabel() {
      return this.isAutonomy
        ? "Autonomy"
        : displayGroupFormat(this.classData.format);
    },
    frequencyLabel() {
      return displayGroupFrequency(this.classData.frequency);
    },
    statusLabel() {
      return displayGroupStatus(this.classData.status);
    },
    statusTone() {
      const status = +this.classData.status;
      if (status === 1) return "active";
      if (status === 0) return "waiting";
      return "off";
    },
    sinceLabel() {
      return this.classData.createdAt || "";
    },
    schedule() {
      return normalizeClassTimes(this.classData.weekDays, this.classData.hours);
    },
    teacher() {
      const t = this.classData.teacher || {};
      const name = t.full_name || t.name || this.$t("overview.noTeacher");
      return {
        name,
        avatar: t.avatar || "",
        initials: initialsFromName(name),
      };
    },
    currentUser() {
      return this.$store.getters.user || {};
    },
    peers() {
      const members = Array.isArray(this.classData.content)
        ? this.classData.content.filter((m) => +m.status === 1)
        : [];

      return members
        .map((m) => {
          const isSelf = this.isSelf(m);
          const name = m.name || this.$t("classView.classmate");
          return {
            key: m.id || m.email || name,
            name,
            label: isSelf ? this.$t("classView.you") : name.split(" ")[0],
            avatar: m.avatar || "",
            initials: initialsFromName(name),
            isSelf,
          };
        })
        .sort((a, b) => (a.isSelf === b.isSelf ? 0 : a.isSelf ? -1 : 1));
    },
    peersCaption() {
      const total = this.peers.length;
      const hasSelf = this.peers.some((p) => p.isSelf);
      const others = hasSelf ? total - 1 : total;

      if (!total) return this.$t("classView.noPeers");
      if (hasSelf && others === 0) return this.$t("classView.onlyYou");
      if (hasSelf) {
        return this.$tc("classView.youAnd", others, { count: others });
      }
      return this.$tc("classView.others", others, { count: others });
    },
    monthOptions() {
      return this.attendanceAvailableMonths.map((m) => ({
        text: formatMonthYear(m.month, m.year) || m.label,
        value: m.key,
      }));
    },
    summary() {
      const editable = this.attendanceEvents.filter((e) => !e.isFuture);
      const futureCount = this.attendanceEvents.length - editable.length;
      const totalLessons = editable.length;
      const presentCount = editable.filter((e) => +e.status === 1).length;
      const absentCount = editable.filter((e) => +e.status === 2).length;
      const pendingCount = Math.max(
        totalLessons - presentCount - absentCount,
        0
      );

      return {
        totalLessons,
        presentCount,
        absentCount,
        pendingCount,
        futureCount,
        presentPercentage: totalLessons
          ? Number(((presentCount / totalLessons) * 100).toFixed(0))
          : 0,
      };
    },
    scorePct() {
      return `${this.summary.presentPercentage}%`;
    },
  },

  methods: {
    isSelf(member) {
      const u = this.currentUser;
      if (u.id != null && member.id != null)
        return String(u.id) === String(member.id);
      if (u.email && member.email)
        return u.email.toLowerCase() === member.email.toLowerCase();
      return false;
    },
    weekDayShort(weekDay) {
      return weekDayShortLabel(weekDay);
    },
    resetAttendanceState() {
      this.attendanceRequestToken += 1;
      this.attendanceAvailableMonths = [];
      this.attendanceEvents = [];
      this.selectedAttendanceMonthKey = null;
    },
    buildAttendanceMonthKey(month, year) {
      if (!month || !year) return null;
      return `${year}-${String(month).padStart(2, "0")}`;
    },
    parseAttendanceMonthKey(monthKey) {
      if (!monthKey || !monthKey.includes("-")) return null;
      const [year, month] = monthKey.split("-").map(Number);
      if (!year || !month) return null;
      return { month, year };
    },
    applyAttendanceResponse(data) {
      this.attendanceAvailableMonths = Array.isArray(data?.availableMonths)
        ? data.availableMonths
        : [];
      this.selectedAttendanceMonthKey = this.buildAttendanceMonthKey(
        data?.month,
        data?.year
      );
      this.attendanceEvents = Array.isArray(data?.events) ? data.events : [];
    },
    async loadAttendance() {
      // O id da turma Autonomy ("autonomy:<aluno>") não existe no banco: a
      // chamada voltava 400 e o aluno via "Não foi possível carregar sua
      // frequência" toda vez que abria a página.
      if (!this.item?.id || this.item.isAutonomy) {
        this.resetAttendanceState();
        return;
      }

      const requestToken = ++this.attendanceRequestToken;
      this.loadingAttendance = true;

      try {
        const selectedPeriod = this.parseAttendanceMonthKey(
          this.selectedAttendanceMonthKey
        );
        const res = await customerApi.getStudyGroupAttendance(this.item.id, {
          month: selectedPeriod?.month,
          year: selectedPeriod?.year,
        });

        if (requestToken !== this.attendanceRequestToken) return;

        this.applyAttendanceResponse(res.data);
      } catch (error) {
        this.resetAttendanceState();
        this.$root.$emit("display-alert", {
          text: this.$t("classView.loadFailed"),
          type: 1,
        });
      } finally {
        this.loadingAttendance = false;
      }
    },
    async handleAttendanceMonthChange(value) {
      this.selectedAttendanceMonthKey = value;
      await this.loadAttendance();
    },
    parseEventDate(value) {
      if (!value) return null;
      const date = value instanceof Date ? value : new Date(value);
      return Number.isNaN(date.getTime()) ? null : date;
    },
    cellState(ev) {
      if (ev?.isFuture) return "future";
      if (+ev?.status === 1) return "present";
      if (+ev?.status === 2) return "absent";
      return "pending";
    },
    cellStatusLabel(ev) {
      if (ev?.isFuture) return this.$t("attendanceTab.futureLesson");
      if (+ev?.status === 1) return this.$t("enums.attendance.present");
      if (+ev?.status === 2) return this.$t("enums.attendance.absent");
      return this.$t("classView.noRecord");
    },
    cellWeekday(ev) {
      const date = this.parseEventDate(ev?.eventDate);
      if (!date) return "—";
      return new Intl.DateTimeFormat(languageTag(), { weekday: "short" })
        .format(date)
        .replace(".", "")
        .toUpperCase();
    },
    cellDay(ev) {
      const date = this.parseEventDate(ev?.eventDate);
      if (!date) return "--";
      return String(date.getDate()).padStart(2, "0");
    },
    cellFullDate(ev) {
      const date = this.parseEventDate(ev?.eventDate);
      if (!date) return "";
      return new Intl.DateTimeFormat(languageTag(), {
        day: "2-digit",
        month: "2-digit",
      }).format(date);
    },
  },
};
</script>

<style scoped>
.sclass {
  --ink: var(--dk-ink, #231a2e);
  --muted: var(--dk-muted, #6b6472);
  --paper: var(--dk-paper, #fbf7fa);
  --line: rgba(var(--dk-ink-rgb, 35, 26, 46), 0.09);
  --pink: var(--dk-pink, #e8276f);
  --teal: var(--dk-teal, #00a379);
  --red: var(--dk-red, #d52323);
  --amber: var(--dk-ember, #ff5d2a);
  width: 100%;
  padding: 26px 28px;
  background: var(--dk-surface, #fff);
  border: 1px solid var(--line);
  border-radius: 22px;
  box-shadow: 0 22px 48px -40px rgba(var(--dk-shadow-rgb, 35, 26, 46), 0.55);
  color: var(--ink);
}

.sclass-display {
  font-family: "Poppins", sans-serif;
  letter-spacing: -0.01em;
}

/* ---------- Cabeçalho ---------- */
.sclass__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding-left: 16px;
  border-left: 3px solid var(--pink);
}
.sclass__eyebrow {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--pink);
}
.sclass__level {
  margin: 4px 0 0;
  font-size: clamp(1.7rem, 3.4vw, 2.3rem);
  font-weight: 700;
  line-height: 1.05;
  color: var(--ink);
}
.sclass__meta {
  margin-top: 6px;
  font-size: 0.86rem;
  color: var(--muted);
}
.sclass__status {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}
.sclass__status-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: currentColor;
}
.sclass__status--active {
  background: rgba(0, 163, 121, 0.12);
  color: var(--dk-teal-deep, #007f5e);
}
.sclass__status--waiting {
  background: rgba(255, 93, 42, 0.12);
  color: var(--dk-ember-deep, #b5421e);
}
.sclass__status--off {
  background: rgba(var(--dk-ink-rgb, 35, 26, 46), 0.08);
  color: var(--muted);
}

/* ---------- Grid quando/quem ---------- */
.sclass__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-top: 22px;
}
.sclass__block {
  padding: 18px;
  border-radius: 16px;
  background: var(--paper);
  border: 1px solid var(--line);
}
.sclass__block-title {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--muted);
  margin-bottom: 12px;
}
.sclass__muted {
  font-size: 0.85rem;
  color: var(--muted);
  line-height: 1.45;
}

.sclass__slots {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.sclass__slot {
  display: flex;
  align-items: center;
  gap: 12px;
}
.sclass__slot-day {
  flex: 0 0 auto;
  min-width: 46px;
  padding: 4px 0;
  text-align: center;
  border-radius: 8px;
  background: rgba(232, 39, 111, 0.1);
  color: var(--pink);
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.sclass__slot-time {
  font-size: 0.98rem;
  font-weight: 700;
  color: var(--ink);
  font-variant-numeric: tabular-nums;
}
.sclass__slot-dash {
  color: var(--muted);
  margin: 0 2px;
}

.sclass__teacher {
  display: flex;
  align-items: center;
  gap: 14px;
}
.sclass__teacher-name {
  font-size: 1rem;
  font-weight: 700;
  color: var(--ink);
}

/* ---------- Avatares ---------- */
.sclass__avatar {
  flex: 0 0 auto;
  width: 46px;
  height: 46px;
  border-radius: 50%;
  overflow: hidden;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    160deg,
    var(--dk-pink, #ff5595),
    var(--dk-pink, #e8276f)
  );
  color: #fff;
  font-size: 0.86rem;
  font-weight: 800;
}
.sclass__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.sclass__avatar--teacher {
  width: 52px;
  height: 52px;
  background: linear-gradient(
    160deg,
    var(--dk-grape, #b951e7),
    var(--dk-grape, #a826e1)
  );
}

/* ---------- Colegas ---------- */
.sclass__peers {
  margin-top: 22px;
}
.sclass__peers-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.sclass__peer {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 5px 14px 5px 5px;
  border-radius: 999px;
  background: var(--paper);
  border: 1px solid var(--line);
}
.sclass__peer .sclass__avatar {
  width: 32px;
  height: 32px;
  font-size: 0.72rem;
}
.sclass__peer-name {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--ink);
}
.sclass__peer--self {
  border-color: rgba(232, 39, 111, 0.4);
  background: rgba(232, 39, 111, 0.06);
}
.sclass__peer--self .sclass__peer-name {
  color: var(--pink);
  font-weight: 700;
}
.sclass__peers-caption {
  margin-top: 10px;
  font-size: 0.82rem;
  color: var(--muted);
}

/* ---------- Frequência ---------- */
.sclass__freq {
  margin-top: 24px;
  padding-top: 22px;
  border-top: 1px solid var(--line);
}
.sclass__freq-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.sclass__freq-head .sclass__block-title {
  margin-bottom: 0;
}
.sclass__month {
  max-width: 190px;
}
.sclass__freq-body {
  display: flex;
  align-items: stretch;
  gap: 20px;
  margin-top: 16px;
}
.sclass__score {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding-right: 20px;
  border-right: 1px solid var(--line);
}
.sclass__score-value {
  font-size: 2.8rem;
  font-weight: 700;
  line-height: 1;
  color: var(--teal);
}
.sclass__score-label {
  margin-top: 2px;
  font-size: 0.74rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--muted);
}
.sclass__strip {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 6px;
  align-items: center;
}
.sclass__cell {
  flex: 0 0 auto;
  width: 52px;
  padding: 8px 0;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  border: 1px solid transparent;
}
.sclass__cell-wd {
  font-size: 0.6rem;
  font-weight: 700;
  letter-spacing: 0.04em;
}
.sclass__cell-day {
  font-size: 1.05rem;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
}
.sclass__cell--present {
  background: var(--teal);
  color: #fff;
}
.sclass__cell--absent {
  background: var(--red);
  color: #fff;
}
.sclass__cell--pending {
  background: rgba(var(--dk-ink-rgb, 35, 26, 46), 0.06);
  color: var(--muted);
}
.sclass__cell--future {
  background: var(--dk-surface, #fff);
  border-color: rgba(255, 93, 42, 0.4);
  color: var(--dk-ember-deep, #b5421e);
}

.sclass__legend {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
}
.sclass__tag {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 0.76rem;
  font-weight: 700;
}
.sclass__tag::before {
  content: "";
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 7px;
  background: currentColor;
}
.sclass__tag--present {
  background: rgba(0, 163, 121, 0.12);
  color: var(--dk-teal-deep, #007f5e);
}
.sclass__tag--absent {
  background: rgba(213, 35, 35, 0.12);
  color: var(--dk-red, #a61b1b);
}
.sclass__tag--pending {
  background: rgba(var(--dk-ink-rgb, 35, 26, 46), 0.06);
  color: var(--muted);
}
.sclass__tag--future {
  background: rgba(255, 93, 42, 0.12);
  color: var(--dk-ember-deep, #b5421e);
}
.sclass__note {
  margin-top: 14px;
  font-size: 0.8rem;
  color: var(--muted);
  line-height: 1.5;
}

/* ---------- Responsivo ---------- */
@media (max-width: 720px) {
  .sclass {
    padding: 22px 18px;
  }
  .sclass__grid {
    grid-template-columns: 1fr;
  }
  .sclass__freq-body {
    flex-direction: column;
    gap: 14px;
  }
  .sclass__score {
    flex-direction: row;
    align-items: baseline;
    gap: 8px;
    padding-right: 0;
    padding-bottom: 4px;
    border-right: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .sclass__cell {
    transition: none;
  }
}
</style>
